/**
 * CVO Officers View
 * Manages complaint officers and their assignments
 * Self-contained view with its own API calls, state management, and dialogs
 */

import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';


import {
  createOfficer,
  fetchOfficers,
  updateOfficer,
  deleteOfficer,
  deactivateOfficer,
  selectOfficers,
  selectOfficerLoading,
  selectOfficerError,
} from '../../store/slices/officerSlice';
import {
  selectDepartments,
  selectOfficerDesignations,
  selectAuthorityPrivileges,
} from '../../store/slices/settingsSlice';
import { OfficerDialog, ConfirmDialog } from '../../components/dialogs';
import { exportToPDF, exportToExcel, exportToCSV } from '../../utils/tableExport';
import { obfuscateEmail } from '../../utils/emailObfuscation';
import TablePagination from '../../components/TablePagination';

const OfficersView = ({ onOfficerCasesClick, hideDepartmentField = false, defaultDepartment = null, role = 'cvo' }) => {
  const dispatch = useDispatch();

  // Redux state selectors
  const officersFromRedux = useSelector(selectOfficers);
  const officersLoading = useSelector(selectOfficerLoading);
  const officersError = useSelector(selectOfficerError);
  const departmentsRaw = useSelector(selectDepartments);
  const officerDesignationsRaw = useSelector(selectOfficerDesignations);
  const authorityPrivilegesRaw = useSelector(selectAuthorityPrivileges);

  // Local state
  const [showAddOfficer, setShowAddOfficer] = useState(false);
  const [showEditOfficer, setShowEditOfficer] = useState(false);
  const [editOfficer, setEditOfficer] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [officerToDelete, setOfficerToDelete] = useState(null);
  const [notification, setNotification] = useState({ show: false, message: '', type: 'success' });
  const [searchTerm, setSearchTerm] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [showDownloadDropdown, setShowDownloadDropdown] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const dropdownRef = useRef(null);
  const downloadDropdownRef = useRef(null);
  const [newOfficer, setNewOfficer] = useState({
    employmentId: '',
    name: '',
    designation: '',
    email: '',
    phone: '',
    department: hideDepartmentField && defaultDepartment ? defaultDepartment : '',
    menuIds: []
  });

  // Filter only active items
  const filterActive = (items) => {
    if (!Array.isArray(items)) return [];
    return items.filter(item => {
      if (typeof item === 'string') return true;
      return item &&
        (!item.status || item.status === 'Active') &&
        !item.deleted_at;
    });
  };

  const departments = filterActive(departmentsRaw);
  const officerDesignations = filterActive(officerDesignationsRaw);
  const authorityPrivileges = filterActive(authorityPrivilegesRaw);

  // Fetch officers on component mount
  // Note: Settings data (departments, designations, privileges) is loaded globally in CVODashboard
  useEffect(() => {
    dispatch(fetchOfficers({ params: {}, role }));
  }, [dispatch, role]);

  // Transform officer data for DataGrid
  const transformOfficerData = (officer) => {
    let status = 'Inactive';
    if (officer.status === 'Active' || officer.status === 'active' || officer.status === 1) {
      status = 'Active';
    } else if (officer.status === 'Delete' || officer.status === 'delete') {
      status = 'Delete';
    } else if (officer.status === 'Inactive' || officer.status === 'inactive' || officer.status === 0) {
      status = 'Inactive';
    }

    const permissions = officer.assigned_menus || [];

    return {
      id: officer.id,
      employmentId: officer.employee_id || '',
      name: officer.name || '',
      designation: officer.designation_name || '',
      department: officer.departments_name || '',
      email: officer.email || '',
      phone: officer.mobile || '',
      status: status,
      cases: officer.cases_count || officer.assigned_cases_count || 0,
      permissions: permissions,
      _original: officer
    };
  };

  const officers = Array.isArray(officersFromRedux)
    ? officersFromRedux.map(transformOfficerData)
    : [];

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
      if (downloadDropdownRef.current && !downloadDropdownRef.current.contains(event.target)) {
        setShowDownloadDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Get columns for export
  const getColumns = () => [
    { field: 'employmentId', headerName: 'Employment ID' },
    { field: 'name', headerName: 'Name' },
    { field: 'designation', headerName: 'Designation' },
    { field: 'email', headerName: 'Email' },
    { field: 'phone', headerName: 'Phone' },
    { field: 'department', headerName: 'Department' },
    { field: 'status', headerName: 'Status' },
  ];

  // Filter officers based on search term
  const filteredOfficers = officers.filter(officer => {
    if (!searchTerm) return true;

    const searchLower = searchTerm.toLowerCase();
    const name = (officer.name || '').toString().toLowerCase();
    const employmentId = (officer.employmentId || '').toString().toLowerCase();
    const email = (officer.email || '').toString().toLowerCase();
    const designation = (officer.designation || '').toString().toLowerCase();
    const department = (officer.department || '').toString().toLowerCase();
    const phone = (officer.phone || '').toString().toLowerCase();

    return name.includes(searchLower) ||
      employmentId.includes(searchLower) ||
      email.includes(searchLower) ||
      designation.includes(searchLower) ||
      department.includes(searchLower) ||
      phone.includes(searchLower);
  });

  // Pagination Logic
  const totalItems = filteredOfficers.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const paginatedOfficers = filteredOfficers.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  // Get filtered data for export
  const getFilteredData = () => {
    return filteredOfficers.map(officer => ({
      employmentId: officer.employmentId || '',
      name: officer.name || '',
      designation: officer.designation || '',
      email: officer.email || '',
      phone: officer.phone || '',
      department: officer.department || '',
      status: officer.status || '',
    }));
  };

  const handleExportCSV = () => {
    try {
      const filteredData = getFilteredData();
      exportToCSV(filteredData, getColumns(), 'officers');
      setShowDownloadDropdown(false);
    } catch (error) {
      console.error('Error exporting CSV:', error);
      alert('Failed to export CSV. Please try again.');
    }
  };

  const handleExportExcel = () => {
    try {
      const filteredData = getFilteredData();
      exportToExcel(filteredData, getColumns(), 'officers');
      setShowDownloadDropdown(false);
    } catch (error) {
      console.error('Error exporting Excel:', error);
      alert('Failed to export Excel. Please try again.');
    }
  };

  const handleExportPDF = () => {
    try {
      const filteredData = getFilteredData();
      exportToPDF(filteredData, getColumns(), 'officers');
      setShowDownloadDropdown(false);
    } catch (error) {
      console.error('Error exporting PDF:', error);
      alert('Failed to export PDF. Please try again.');
    }
  };

  // Show notification helper
  const showNotification = (message, type = 'success') => {
    setNotification({ show: true, message, type });
    setTimeout(() => {
      setNotification({ show: false, message: '', type: 'success' });
    }, 3000);
  };

  // Handler for adding officer
  const handleAddOfficer = () => {
    // Auto-fill department if defaultDepartment is provided and hideDepartmentField is true
    // Find matching department in dropdown list
    if (hideDepartmentField && defaultDepartment && departments.length > 0) {
      const matchingDepartment = departments.find(dept => {
        const deptId = typeof dept === 'object' ? dept.id : dept;
        return String(deptId) === String(defaultDepartment);
      });

      if (matchingDepartment) {
        const deptId = typeof matchingDepartment === 'object' ? matchingDepartment.id : matchingDepartment;
        setNewOfficer(prev => ({
          ...prev,
          department: String(deptId)
        }));
      }
    }
    setShowAddOfficer(true);
  };

  // Handler for saving new officer
  const handleSaveOfficer = async () => {
    try {
      if (!newOfficer.employmentId || !newOfficer.name || !newOfficer.designation ||
        !newOfficer.email || !newOfficer.phone) {
        showNotification('Please fill all required fields', 'error');
        return;
      }

      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(newOfficer.email)) {
        showNotification('Please enter a valid email address', 'error');
        return;
      }

      const phoneRegex = /^[0-9]{10}$/;
      if (!phoneRegex.test(newOfficer.phone)) {
        showNotification('Please enter a valid 10-digit phone number', 'error');
        return;
      }

      // Ensure department is set if hideDepartmentField is true
      const officerData = { ...newOfficer };
      if (hideDepartmentField && defaultDepartment && !officerData.department) {
        officerData.department = defaultDepartment;
      }

      const result = await dispatch(createOfficer({ officerData, role }));

      if (createOfficer.fulfilled.match(result)) {
        showNotification('Officer created successfully', 'success');
        setShowAddOfficer(false);
        setNewOfficer({
          employmentId: '',
          name: '',
          designation: '',
          email: '',
          phone: '',
          department: hideDepartmentField && defaultDepartment ? defaultDepartment : '',
          menuIds: []
        });
        dispatch(fetchOfficers({ params: {}, role }));
      } else {
        showNotification(result.payload || 'Failed to create officer', 'error');
      }
    } catch (error) {
      showNotification('Failed to create officer', 'error');
    }
  };

  // Handler for editing officer
  const handleEditOfficer = (officer) => {
    const menuIds = officer.permissions?.map(menu => menu.id) ||
      officer._original?.menu_ids || [];

    const formData = {
      id: officer.id,
      employmentId: officer.employmentId,
      name: officer.name,
      designation: officer._original?.designation_id || '',
      email: officer.email,
      phone: officer.phone,
      department: hideDepartmentField && defaultDepartment
        ? defaultDepartment
        : (officer._original?.department_id || ''),
      status: officer.status,
      menuIds: menuIds
    };
    setEditOfficer(formData);
    setShowEditOfficer(true);
  };

  // Handler for updating officer
  const handleUpdateOfficer = async () => {
    try {
      if (!editOfficer.employmentId || !editOfficer.name || !editOfficer.designation ||
        !editOfficer.email || !editOfficer.phone) {
        showNotification('Please fill all required fields', 'error');
        return;
      }

      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(editOfficer.email)) {
        showNotification('Please enter a valid email address', 'error');
        return;
      }

      const phoneRegex = /^[0-9]{10}$/;
      if (!phoneRegex.test(editOfficer.phone)) {
        showNotification('Please enter a valid 10-digit phone number', 'error');
        return;
      }

      // Ensure department is set if hideDepartmentField is true
      const officerData = { ...editOfficer };
      if (hideDepartmentField && defaultDepartment && !officerData.department) {
        officerData.department = defaultDepartment;
      }

      const result = await dispatch(updateOfficer({
        id: editOfficer.id,
        data: officerData,
        role
      }));

      if (updateOfficer.fulfilled.match(result)) {
        showNotification('Officer updated successfully', 'success');
        setShowEditOfficer(false);
        setEditOfficer(null);
        dispatch(fetchOfficers({ params: {}, role }));
      } else {
        showNotification(result.payload || 'Failed to update officer', 'error');
      }
    } catch (error) {
      showNotification('Failed to update officer', 'error');
    }
  };

  // Handler for deactivating officer
  const handleDeactivateOfficer = async (officer) => {
    const result = await dispatch(deactivateOfficer({ id: officer.id, role }));
    if (deactivateOfficer.fulfilled.match(result)) {
      showNotification('Officer deactivated successfully', 'success');
      dispatch(fetchOfficers({ params: {}, role }));
    } else {
      showNotification('Failed to deactivate officer', 'error');
    }
  };

  // Handler for initiating delete
  const handleDeleteClick = (officer) => {
    setOfficerToDelete(officer);
    setShowDeleteConfirm(true);
  };

  // Handler for confirming delete
  const handleConfirmDelete = async () => {
    if (!officerToDelete) return;

    try {
      const result = await dispatch(deleteOfficer({ id: officerToDelete.id, role }));

      if (deleteOfficer.fulfilled.match(result)) {
        showNotification('Officer deleted successfully', 'success');
        setShowDeleteConfirm(false);
        setOfficerToDelete(null);
        dispatch(fetchOfficers({ params: {}, role }));
      } else {
        showNotification(result.payload || 'Failed to delete officer', 'error');
      }
    } catch (error) {
      showNotification('Failed to delete officer', 'error');
    }
  };

  // Handler for canceling delete
  const handleCancelDelete = () => {
    setShowDeleteConfirm(false);
    setOfficerToDelete(null);
  };


  return (
    <>
      <div className="h-full flex flex-col min-h-0">
        {/* Header with Search (Left) and Buttons (Right) */}
        <div className="flex justify-between items-center pb-4 flex-shrink-0 gap-4">
          {/* Search Bar - Left Aligned */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 w-96">
            <div className="flex items-center space-x-3">
              <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="flex-1 outline-none text-gray-700"
                placeholder="Search by name, employment ID, email, designation, department, or phone..."
              />
            </div>
          </div>

          {/* Buttons - Right Aligned */}
          <div className="flex items-center gap-3">
            {/* Download Button with Dropdown */}
            <div className="relative" ref={downloadDropdownRef}>
              <button
                onClick={() => setShowDownloadDropdown(!showDownloadDropdown)}
                className="px-6 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors duration-200 font-medium flex items-center space-x-2"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                </svg>
                <span>Download</span>
                <svg className={`w-4 h-4 transition-transform ${showDownloadDropdown ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {/* Download Dropdown Menu */}
              {showDownloadDropdown && (
                <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 z-50">
                  <div className="py-1">
                    <button
                      onClick={handleExportCSV}
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center space-x-2"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      <span>Download CSV</span>
                    </button>
                    <button
                      onClick={handleExportExcel}
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center space-x-2"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      <span>Download Excel</span>
                    </button>
                    <button
                      onClick={handleExportPDF}
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center space-x-2"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                      </svg>
                      <span>Download PDF</span>
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Add Officer Button */}
            <button
              onClick={handleAddOfficer}
              className="px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors duration-200 font-medium flex items-center space-x-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
              <span>Add Officer</span>
            </button>
          </div>
        </div>

        {/* Officers Table */}
        <div className="bg-white rounded-xl shadow-lg border border-gray-100 flex-1 flex flex-col overflow-hidden min-h-0">
          {/* Error Display */}
          {officersError && (
            <div className="p-4 bg-red-50 border-l-4 border-red-400 flex-shrink-0">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm text-red-700">
                    <strong>Error loading officers:</strong> {officersError}
                  </p>
                </div>
              </div>
            </div>
          )}

          <div className="flex-1 overflow-auto">
            <table className="min-w-full divide-y divide-gray-200 relative">
              <thead className="bg-gray-50 sticky top-0 z-10">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                    Employment ID
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                    Name
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                    Designation
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                    Email
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                    Phone
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                    Permissions
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                    Status
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                    Cases
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {officersLoading ? (
                  <tr>
                    <td colSpan="9" className="px-6 py-12 text-center text-gray-500">
                      <div className="flex flex-col items-center">
                        <svg className="animate-spin h-8 w-8 text-primary-600 mb-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        <p className="text-gray-600">Loading officers...</p>
                      </div>
                    </td>
                  </tr>
                ) : filteredOfficers.length === 0 ? (
                  <tr>
                    <td colSpan="9" className="px-6 py-12 text-center text-gray-500">
                      <p className="text-lg font-medium text-gray-900">No officers found</p>
                      <p className="text-sm text-gray-500">Try adjusting your search criteria</p>
                    </td>
                  </tr>
                ) : (
                  paginatedOfficers.map((row) => (
                    <tr key={row.id} className="hover:bg-gray-50 transition-colors duration-150">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {row.employmentId}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {row.name}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {row.designation}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {obfuscateEmail(row.email)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {row.phone}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">
                        <div className="flex flex-wrap gap-1">
                          {row.permissions && row.permissions.length > 0 ? (
                            row.permissions.map(menu => (
                              <span
                                key={menu.id}
                                className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200"
                              >
                                {menu.name}
                              </span>
                            ))
                          ) : <span className="text-gray-400 text-sm">No permissions</span>}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {(() => {
                          const status = row.status;
                          let styles = "bg-gray-100 text-gray-800 border-gray-200";
                          if (status === 'Active') styles = "bg-green-50 text-green-700 border-green-200";
                          else if (status === 'Delete') styles = "bg-red-50 text-red-700 border-red-200";
                          else if (status === 'Inactive') styles = "bg-yellow-50 text-yellow-800 border-yellow-200";

                          return (
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${styles}`}>
                              {status}
                            </span>
                          );
                        })()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <button className="text-blue-600 hover:text-blue-800 underline" onClick={() => onOfficerCasesClick && onOfficerCasesClick(row)}>
                          {row.cases}
                        </button>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex items-center space-x-3">
                          <button
                            onClick={() => handleEditOfficer(row)}
                            className="text-blue-600 hover:text-blue-900 transition-colors"
                            title="Edit Officer"
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                              <path strokeLinecap="round" strokeLinejoin="round" d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L10.582 16.07a4.5 4.5 0 0 1-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 0 1 1.13-1.897l8.932-8.931Zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0 1 15.75 21H5.25A2.25 2.25 0 0 1 3 18.75V8.25A2.25 2.25 0 0 1 5.25 6H10" />
                            </svg>
                          </button>
                          <button
                            onClick={() => handleDeleteClick(row)}
                            className="text-red-600 hover:text-red-900 transition-colors"
                            title="Delete Officer"
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                              <path strokeLinecap="round" strokeLinejoin="round" d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0" />
                            </svg>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <TablePagination
            currentPage={currentPage}
            totalPages={totalPages}
            totalItems={totalItems}
            itemsPerPage={itemsPerPage}
            onPageChange={setCurrentPage}
            onItemsPerPageChange={(newSize) => {
              setItemsPerPage(newSize);
              setCurrentPage(1);
            }}
          />
        </div>
      </div>

      {/* Add Officer Dialog */}
      <OfficerDialog
        isOpen={showAddOfficer}
        mode="add"
        officer={newOfficer}
        onOfficerChange={setNewOfficer}
        onSubmit={handleSaveOfficer}
        onCancel={() => setShowAddOfficer(false)}
        designations={officerDesignations}
        departments={departments}
        authorityPrivileges={authorityPrivileges}
        hideDepartmentField={hideDepartmentField}
        defaultDepartment={defaultDepartment}
      />

      {/* Edit Officer Dialog */}
      <OfficerDialog
        isOpen={showEditOfficer && !!editOfficer}
        mode="edit"
        officer={editOfficer}
        onOfficerChange={setEditOfficer}
        onSubmit={handleUpdateOfficer}
        onCancel={() => {
          setShowEditOfficer(false);
          setEditOfficer(null);
        }}
        designations={officerDesignations}
        departments={departments}
        authorityPrivileges={authorityPrivileges}
        hideDepartmentField={hideDepartmentField}
        defaultDepartment={defaultDepartment}
      />

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        isOpen={showDeleteConfirm}
        title="Delete Officer"
        message={`Are you sure you want to delete ${officerToDelete?.name || 'this officer'}? `}
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
        loading={officersLoading}
        confirmText="Delete"
        cancelText="Cancel"
      />

      {/* Notification Toast */}
      {notification.show && (
        <div className="fixed bottom-4 right-4 z-50 animate-slide-up">
          <div className={`rounded-lg shadow-xl p-4 flex items-center space-x-3 ${notification.type === 'success' ? 'bg-green-500' : 'bg-red-500'
            } text-white`}>
            <div>
              {notification.type === 'success' ? (
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              ) : (
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              )}
            </div>
            <div className="flex-1">
              <p className="font-medium">{notification.message}</p>
            </div>
            <button
              onClick={() => setNotification({ show: false, message: '', type: 'success' })}
              className="text-white hover:text-gray-200"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default OfficersView;

